/**
 * Terms of Service Page — Public
 * Minimal but legally sound placeholder.
 */
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import PublicLanguageSwitcher from '../components/PublicLanguageSwitcher';

const TermsOfServicePage = () => (
  <div className="min-h-screen bg-[#111318] text-[#CBD5E1]">
    <nav className="border-b border-[rgba(255,255,255,0.07)] bg-[#181B23]">
      <div className="max-w-4xl mx-auto px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/" className="text-[#8B95A5] hover:text-white transition-colors"><ArrowLeft size={18} /></Link>
          <Link to="/" className="font-['Syne'] text-white font-semibold text-base">CHARSTAN</Link>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/privacy" className="text-[#8B95A5] hover:text-[#38BDF8] text-sm">Privacy</Link>
          <Link to="/security-policy" className="text-[#8B95A5] hover:text-[#38BDF8] text-sm">Security</Link>
          <PublicLanguageSwitcher />
        </div>
      </div>
    </nav>

    <main className="max-w-4xl mx-auto px-6 py-16" data-testid="terms-page">
      <div className="flex items-center gap-3 mb-2">
        <FileText size={24} className="text-[#38BDF8]" />
        <h1 className="font-['Syne'] text-3xl font-bold text-white">Terms of Service</h1>
      </div>
      <p className="text-[#8B95A5] text-sm mb-10">Last updated: April 2026</p>

      <div className="space-y-8 text-sm leading-relaxed">
        <section>
          <h2 className="text-white font-semibold text-base mb-2">1. Acceptance of Terms</h2>
          <p>By accessing or using the CHARSTAN Intelligence Platform ("Platform"), you agree to be bound by these Terms of Service. If you do not agree to these terms, you may not use the Platform. These terms apply to all users, including administrators, auditors, and client users.</p>
        </section>

        <section>
          <h2 className="text-white font-semibold text-base mb-2">2. Description of Service</h2>
          <p>CHARSTAN provides a cloud-based Intelligence Platform for regulated industries. The Platform includes compliance assessment tools, audit management, document control, governance frameworks, and AI-assisted intelligence features. The Platform is provided "as is" and is subject to change.</p>
        </section>

        <section>
          <h2 className="text-white font-semibold text-base mb-2">3. User Accounts and Access</h2>
          <p>You are responsible for maintaining the confidentiality of your account credentials. You must not share your login credentials or allow unauthorized access to your account. You agree to notify CHARSTAN immediately of any unauthorized use of your account. Role-based access controls are enforced; do not attempt to circumvent them.</p>
        </section>

        <section>
          <h2 className="text-white font-semibold text-base mb-2">4. Acceptable Use</h2>
          <p>You agree to use the Platform only for lawful purposes and in accordance with these Terms. You must not: (a) upload malicious content; (b) attempt to gain unauthorized access to any systems; (c) use automated tools to scrape or extract data; (d) reverse engineer or decompile any part of the Platform; (e) use the Platform in any way that could impair its operation.</p>
        </section>

        <section>
          <h2 className="text-white font-semibold text-base mb-2">5. Data Ownership and Confidentiality</h2>
          <p>You retain ownership of all data you submit to the Platform. CHARSTAN does not claim ownership of your content. All client data is treated as confidential and is subject to the terms of your service agreement. CHARSTAN will not share your data with third parties except as required by law or as specified in our Privacy Policy.</p>
        </section>

        <section>
          <h2 className="text-white font-semibold text-base mb-2">6. Intellectual Property</h2>
          <p>The Platform, including its software, design, branding, and AI models, is the intellectual property of CHARSTAN. You are granted a limited, non-exclusive, non-transferable license to use the Platform for its intended purpose. This license does not include the right to modify, distribute, or create derivative works.</p>
        </section>

        <section>
          <h2 className="text-white font-semibold text-base mb-2">7. Compliance Disclaimer</h2>
          <p>The Platform provides compliance assessment tools and readiness reports as advisory aids. These do not constitute legal advice, regulatory approval, or certification. Users remain solely responsible for ensuring compliance with all applicable laws, regulations, and standards in their respective jurisdictions. CHARSTAN does not guarantee that use of the Platform will result in regulatory compliance.</p>
        </section>

        <section>
          <h2 className="text-white font-semibold text-base mb-2">8. Limitation of Liability</h2>
          <p>To the maximum extent permitted by law, CHARSTAN shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Platform. CHARSTAN's total liability shall not exceed the amount paid by you for the Platform in the twelve months preceding the claim.</p>
        </section>

        <section>
          <h2 className="text-white font-semibold text-base mb-2">9. Termination</h2>
          <p>CHARSTAN may suspend or terminate your access at any time for violation of these Terms or for any other reason with reasonable notice. Upon termination, your right to use the Platform ceases immediately. You may request export of your data within 30 days of termination.</p>
        </section>

        <section>
          <h2 className="text-white font-semibold text-base mb-2">10. Governing Law</h2>
          <p>These Terms are governed by and construed in accordance with the laws of the State of California, United States, without regard to conflict of law principles. For users in Brazil, applicable provisions of the Brazilian Civil Code (Lei 10.406/2002) and the LGPD (Lei 13.709/2018) shall also apply.</p>
        </section>

        <section>
          <h2 className="text-white font-semibold text-base mb-2">11. Contact</h2>
          <p>For questions about these Terms, contact us at <a href="mailto:legal@charstan.com" className="text-[#38BDF8] hover:underline">legal@charstan.com</a> or write to CHARSTAN, Los Angeles, CA 91342, United States.</p>
        </section>
      </div>

      <div className="mt-12 pt-6 border-t border-[rgba(255,255,255,0.07)] text-center text-[#8B95A5] text-xs">
        &copy; {new Date().getFullYear()} CHARSTAN. All rights reserved.
      </div>
    </main>
  </div>
);

export default TermsOfServicePage;
